import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Layout, Typography, Tag, Space, Spin, Result, Button, Divider } from 'antd';
import { ArrowLeftOutlined, CalendarOutlined, EyeOutlined, UserOutlined } from '@ant-design/icons';
import { blogAPI } from '../services/api';

const { Header, Content, Footer } = Layout;
const { Title, Paragraph, Text } = Typography;

function formatDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('zh-TW', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return value;
  }
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    blogAPI.getPostBySlug(slug)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res;
        if (!data || data.detail) {
          setError(data?.detail || 'not_found');
        } else {
          setPost(data);
          if (data.meta_title || data.title) {
            document.title = data.meta_title || data.title;
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'load_failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const isRich = post?.content_format === 'rich';

  return (
    <Layout style={{ minHeight: '100vh', background: '#fafafa' }}>
      <Header style={{
        background: '#fff', borderBottom: '1px solid #f0f0f0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', position: 'sticky', top: 0, zIndex: 10,
      }}>
        <Space>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            返回
          </Button>
          <Link to="/" style={{ fontWeight: 600, color: '#1f2937' }}>
            marcom AI 行銷平台
          </Link>
        </Space>
        <Link to="/blog" style={{ color: '#6b7280' }}>所有文章</Link>
      </Header>

      <Content style={{ padding: '32px 16px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" tip="載入文章中..." />
            </div>
          )}

          {!loading && error && (
            <Result
              status="404"
              title="找不到這篇文章"
              subTitle={`slug: ${slug}`}
              extra={<Button type="primary" onClick={() => navigate('/')}>回首頁</Button>}
            />
          )}

          {!loading && post && (
            <article style={{
              background: '#fff', borderRadius: 12, padding: '40px 48px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}>
              {post.featured_image && (
                <img
                  src={post.featured_image}
                  alt={post.title}
                  style={{
                    width: '100%', maxHeight: 360, objectFit: 'cover',
                    borderRadius: 8, marginBottom: 24,
                  }}
                />
              )}

              <Title level={1} style={{ marginTop: 0, marginBottom: 12 }}>
                {post.title}
              </Title>

              <Space size="middle" wrap style={{ marginBottom: 20, color: '#6b7280' }}>
                {post.author && (
                  <span><UserOutlined /> {post.author}</span>
                )}
                {post.published_at && (
                  <span><CalendarOutlined /> {formatDate(post.published_at)}</span>
                )}
                {typeof post.view_count === 'number' && (
                  <span><EyeOutlined /> {post.view_count}</span>
                )}
              </Space>

              {Array.isArray(post.tags) && post.tags.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  {post.tags.map((tag) => (
                    <Tag key={tag} color="blue">{tag}</Tag>
                  ))}
                </div>
              )}

              {post.excerpt && (
                <Paragraph style={{
                  fontSize: 17, color: '#374151', lineHeight: 1.7,
                  borderLeft: '4px solid #3b82f6', paddingLeft: 16,
                  background: '#f9fafb', padding: '12px 16px', borderRadius: 4,
                  margin: '0 0 24px',
                }}>
                  {post.excerpt}
                </Paragraph>
              )}

              <Divider style={{ margin: '8px 0 24px' }} />

              {isRich ? (
                <div
                  className="blog-rich-content"
                  style={{ fontSize: 16, lineHeight: 1.8, color: '#1f2937' }}
                  dangerouslySetInnerHTML={{ __html: post.content || '' }}
                />
              ) : (
                <div style={{ fontSize: 16, lineHeight: 1.9, color: '#1f2937', whiteSpace: 'pre-wrap' }}>
                  {post.content}
                </div>
              )}
            </article>
          )}
        </div>
      </Content>

      <Footer style={{ textAlign: 'center', background: 'transparent', color: '#9ca3af' }}>
        © {new Date().getFullYear()} marcom AI 行銷平台
      </Footer>
    </Layout>
  );
}
